import { Box, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip as MuiTooltip } from '@material-ui/core';
import { Print, Visibility, VisibilityOff } from '@material-ui/icons';
import React, { useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#ed213d', '#b070d6'];

interface BarAndTableProps {
    data: { nome: string, total: number }[];
    name?: string;
    title?: string;
}

export default function BarAndTable({ data, name = '', title = '', ...props }: BarAndTableProps) {
    const [hideEmpty, setHideEmpty] = useState(false)
    const printRef = useRef<HTMLDivElement>(null)

    const onPrint = async () => {
        if (!printRef.current) return;

        try {
            const element = printRef.current;
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
                unit: 'px',
                format: [canvas.width, canvas.height]
            });

            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            pdf.save(`${title || 'relatorio'}.pdf`);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
        }
    }

    if (!data) return null;
    return <Box mt={2}>
        <Box display="flex" alignItems="center" flexDirection="row" justifyContent="space-between">
            <Typography variant="h3">{title}</Typography>
            <Box display="flex" alignItems="center" flexDirection="row">
                <MuiTooltip title={"Imprimir Gráfico"}>
                    <IconButton onClick={() => onPrint()}>
                        <Print />
                    </IconButton>
                </MuiTooltip>
                <MuiTooltip title={hideEmpty ? "Mostrar Vazios" : "Esconder Vazios"}>
                    <IconButton onClick={() => setHideEmpty((prev) => !prev)}>
                        {hideEmpty ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                </MuiTooltip>
            </Box>
        </Box>
        <div ref={printRef}>
            <Grid container spacing={1} alignItems="center" justifyContent="center">
                <Grid item xs={6}>
                    <ResponsiveContainer width="100%" height={500}>
                        <BarChart width={200} height={200} data={data.filter(item => !hideEmpty || item.total != 0)} layout="vertical">
                            <Tooltip />
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="nome" type="category" width={200} />
                            <Bar dataKey="total">
                                {data.map((_, index: number) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Grid>
                <Grid item xs={6}>
                    <TableContainer>
                        <Table size="small" aria-label="a dense table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{name}</TableCell>
                                    <TableCell align="right">Quantidade</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map(row => {
                                    if (hideEmpty && row.total === 0) return null;
                                    return <TableRow key={row.nome}>
                                        <TableCell component="th" scope="row">
                                            {row.nome}
                                        </TableCell>
                                        <TableCell align="right">{row.total}</TableCell>
                                    </TableRow>
                                })}
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        <b>Total</b>
                                    </TableCell>
                                    <TableCell align="right">
                                        <b>{data.reduce((partial, item) => partial + item.total, 0)}</b>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </div>
    </Box >
}
